
export const manTheme = ({
  /** ---------------------------------------------------------
   * COLORS — EXACT MATCH OF MUI (Palette B)
   * --------------------------------------------------------- */
  colors: {
    primary: [
      '#FFE9D6', '#FFD6AF', '#FFC08B', '#FFAB66',
      '#FF9640', '#FB8C00', '#D97600', '#B76200',
      '#944F00', '#723D00'
    ],

    secondary: [
      '#FFF3D6', '#FFE6AF', '#FFD98B', '#FFCB66',
      '#FFBD40', '#FFA000', '#DB8600', '#B76F00',
      '#945900', '#724400'
    ],

    accentPink: ['#FFEBEE', '#FFCDD2', '#FFB8C5', '#FFA5B8', '#FF90AA', '#FF7A9C'],
    accentMint: ['#E8F5E9', '#C8E6C9', '#A5D6A7'],
    accentGold: ['#FFF3E0', '#FFE0B2', '#FFCC80'],
    accentOrange: ['#FFF2E0', '#FFE0CC', '#FFD180'],
    accentAqua: ['#E0F7FA', '#B2EBF2', '#80DEEA'],
  },

  /** ---------------------------------------------------------
   * PRIMARY + SECONDARY
   * --------------------------------------------------------- */
  primaryColor: 'primary',
  primaryShade: 5,

  /** ---------------------------------------------------------
   * BREAKPOINTS — IDENTICAL TO MUI
   * --------------------------------------------------------- */
  breakpoints: {
    xs: '0px',
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1536px',
    xxl: '1920px',
  },

  /** ---------------------------------------------------------
   * TYPOGRAPHY — EXACT RESPONSIVE MATCH
   * --------------------------------------------------------- */
  fontFamily: '"Nunito Sans", sans-serif',
  headings: {
    fontFamily: '"Poppins", sans-serif',
    sizes: {
      h1: { fontWeight: '700', fontSize: '6rem', lineHeight: 1.2 },
      h2: { fontWeight: '600', fontSize: '4.5rem', lineHeight: 1.3 },
      h3: { fontWeight: '500', fontSize: '3.5rem', lineHeight: 1.4 },
      h4: { fontWeight: '500', fontSize: '2.5rem', lineHeight: 1.4 },
      h5: { fontWeight: '500', fontSize: '2rem', lineHeight: 1.5 },
      h6: { fontWeight: '400', fontSize: '1.5rem', lineHeight: 1.5 },
    },
  },

  /** ---------------------------------------------------------
   * RADIUS, SHADOW, BORDER — MATCHED TO MUI
   * --------------------------------------------------------- */
  radius: {
    xs: '10px',
    sm: '12px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    full: '50px',
  },

  shadows: {
    sm: '0 4px 14px rgba(239, 108, 0, 0.08)',
    md: '0 6px 20px rgba(239, 108, 0, 0.12)',
    lg: '0 10px 30px rgba(239, 108, 0, 0.15)',
  },

  /** ---------------------------------------------------------
   * GLOBAL CSS OVERRIDES TO MATCH MUI COMPONENTS
   * --------------------------------------------------------- */
  components: {
    /** ---------------- BUTTON ---------------- */
    Button: {
      styles: {
        root: {
          borderRadius: '50px',
          padding: '10px 20px',
          fontWeight: 600,
          fontFamily: '"Nunito Sans", sans-serif',
          background: 'linear-gradient(135deg, #FB8C00, #FFA000)',
          color: '#fff',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 14px rgba(239, 108, 0, 0.28)',
          '&:hover': {
            transform: 'translateY(-2px)',
            background: 'linear-gradient(135deg, #FFB74D, #FFECB3)',
            boxShadow: '0 6px 20px rgba(239, 108, 0, 0.34)',
          },
        },
      },
    },

    /** ---------------- INPUT/TEXTFIELD ---------------- */
    TextInput: {
      styles: {
        input: {
          borderRadius: '50px',
          padding: '12px 16px',
          background: 'rgba(255, 248, 245, 0.6)',
          borderColor: 'rgba(251, 140, 0, 0.4)',
          '&:focus': { borderColor: '#FB8C00', borderWidth: 2 },
        },
        label: {
          fontSize: '1rem',
          color: '#6B6B6B',
        },
      },
    },

    Select: {
      styles: {
        input: {
          borderRadius: '50px',
          padding: '12px 20px',
          background: 'rgba(255, 248, 245, 0.6)',
          borderColor: 'rgba(251, 140, 0, 0.4)',
          '&:focus': { borderColor: '#FB8C00', borderWidth: 2 },
        },
      },
    },

    /** ---------------- CARD ---------------- */
    Card: {
      styles: {
        root: {
          borderRadius: '24px',
          padding: '24px',
          background: 'linear-gradient(145deg, #FFFFFF, rgba(255,244,238,0.6))',
          border: '1px solid rgba(251, 140, 0, 0.17)',
          boxShadow: '0 10px 30px rgba(239, 108, 0, 0.12)',
        },
      },
    },

    /** ---------------- CHIP ---------------- */
    Chip: {
      styles: {
        root: {
          borderRadius: '50px',
          padding: '0 12px',
          height: '32px',
          color: '#fff',
          background: 'linear-gradient(135deg, #FB8C00, #FFA000)',
          fontSize: '0.875rem',
        },
      },
    },

    /** ---------------- TOOLTIP ---------------- */
    Tooltip: {
      styles: {
        tooltip: {
          background: '#FB8C00',
          color: 'white',
          padding: '8px 12px',
          fontSize: '0.875rem',
          borderRadius: '8px',
        },
      },
    },

    /** ---------------- ALERT ---------------- */
    Alert: {
      styles: {
        root: {
          borderRadius: '16px',
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #FFFFFF, rgba(239,108,0,0.06))',
        },
      },

      variants: {
        success: {
          root: { background: '#FFECB3', color: '#FFFFFF' },
        },
        error: {
          root: { background: '#FFCDD2', color: '#FFFFFF' },
        },
        warning: {
          root: { background: '#FFD180', color: '#FFFFFF' },
        },
        info: {
          root: { background: '#B2EBF2', color: '#FFFFFF' },
        },
      },
    },

    /** ---------------- PAPER ---------------- */
    Paper: {
      styles: {
        root: {
          borderRadius: '16px',
          background: '#FFFFFF',
          boxShadow: '0 4px 14px rgba(239, 108, 0, 0.06)',
        },
      },
    },
  },

  /** ---------------------------------------------------------
   * GLOBAL CSS TO MATCH MUI TYPOGRAPHY COLORS
   * --------------------------------------------------------- */
  globalStyles: (theme) => ({
    body: {
      color: theme.colors.dark[9],
      backgroundColor: '#FFF8F2',
    },
  }),
});
